/**
 * React Query hooks for Talent Review APIs
 * 
 * Usage:
 * const { data: grid, isLoading } = useNineBoxGrid(2025)
 * const { mutate: createPosition } = useCreateNineBoxPosition()
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { talentReviewService, type PlacementBatchInput } from '@/services/api/talentReviewService'
import type { AgendaStepId, SuccessorNominationBatchInput, RoomSuccessorInput, CampusInputWrite, SuccessorStatus,
  SuccessorReadiness,
} from '@/services/api/talentReviewService'
import roleMasterService from '@/services/api/roleMasterService'
import type {
  TalentProfile,
  NineBoxGrid,
  NineBoxPosition,
  NineBoxPositionCreate,
  PerformanceLevel,
  PotentialLevel,
  SuccessionPlan,
  SuccessionPlanCreate,
  SuccessionCandidateCreate,
  KeyPerson,
  KeyPersonCreate,
  RetentionRisk,
  HIPODesignation,
  HIPOCategory,
  BenchStrengthAnalysis,
  TalentReviewSession,
  TalentReviewSessionCreate,
} from '@/services/api/talentReviewService'

// ============================================================================
// QUERY KEYS
// ============================================================================

export const talentKeys = {
  all: ['talent'] as const,
  profiles: () => [...talentKeys.all, 'profiles'] as const,
  profile: (userId: string) => [...talentKeys.profiles(), userId] as const,
  nineBox: () => [...talentKeys.all, '9box'] as const,
  nineBoxGrid: (year: number, orgUnitId?: string) =>
    [...talentKeys.nineBox(), 'grid', year, orgUnitId] as const,
  succession: () => [...talentKeys.all, 'succession'] as const,
  successionList: (filters?: { review_year?: number; org_unit_id?: string; risk_level?: string }) =>
    [...talentKeys.succession(), 'list', filters] as const,
  keyPersons: () => [...talentKeys.all, 'key-persons'] as const,
  keyPersonsList: (filters?: { review_year?: number; retention_risk?: RetentionRisk }) =>
    [...talentKeys.keyPersons(), 'list', filters] as const,
  hipos: () => [...talentKeys.all, 'hipos'] as const,
  hiposList: (filters?: { review_year?: number; category?: HIPOCategory }) =>
    [...talentKeys.hipos(), 'list', filters] as const,
  benchStrength: () => [...talentKeys.all, 'bench-strength'] as const,
  benchStrengthAnalysis: (year: number, level?: string) =>
    [...talentKeys.benchStrength(), year, level] as const,
  sessions: () => [...talentKeys.all, 'sessions'] as const,
  sessionsList: (filters?: { review_year?: number; status?: string }) =>
    [...talentKeys.sessions(), 'list', filters] as const,
}

// ============================================================================
// TALENT PROFILE HOOKS
// ============================================================================

export function useTalentProfile(userId: string) {
  return useQuery({
    queryKey: talentKeys.profile(userId),
    queryFn: () => talentReviewService.getProfile(userId),
    enabled: !!userId,
    staleTime: 10 * 60 * 1000, // 10 minutes - profiles don't change often
  })
}

export function useGenerateTalentProfile() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (userId: string) => talentReviewService.generateProfile(userId),
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: talentKeys.profile(userId) })
    },
  })
}

// ============================================================================
// 9-BOX GRID HOOKS
// ============================================================================

export function useNineBoxGrid(reviewYear: number, orgUnitId?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: talentKeys.nineBoxGrid(reviewYear, orgUnitId),
    queryFn: () => talentReviewService.getNineBoxGrid(reviewYear, orgUnitId),
    // Caller can gate this (e.g. only fetch the 9-box when the Talent tab is
    // actually accessible) — avoids a wasted request for every ordinary PL.
    enabled: !!reviewYear && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
  })
}

export function useCreateNineBoxPosition() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: NineBoxPositionCreate) => talentReviewService.createNineBoxPosition(data),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: talentKeys.nineBoxGrid(data.review_year) })
      queryClient.invalidateQueries({ queryKey: talentKeys.hipos() })
    },
  })
}

export function useUpdateNineBoxPosition() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({
      positionId,
      updates,
    }: {
      positionId: string
      updates: {
        performance_level?: PerformanceLevel
        potential_level?: PotentialLevel
        rationale?: string
      }
    }) => talentReviewService.updateNineBoxPosition(positionId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentKeys.nineBox() })
      queryClient.invalidateQueries({ queryKey: talentKeys.hipos() })
    },
  })
}

// ============================================================================
// SUCCESSION PLANNING HOOKS
// ============================================================================

export function useSuccessionPlans(filters?: {
  review_year?: number
  org_unit_id?: string
  risk_level?: string
}) {
  return useQuery({
    queryKey: talentKeys.successionList(filters),
    queryFn: () => talentReviewService.listSuccessionPlans(filters),
    staleTime: 5 * 60 * 1000,
  })
}

export function useCreateSuccessionPlan() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: SuccessionPlanCreate) => talentReviewService.createSuccessionPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentKeys.succession() })
      queryClient.invalidateQueries({ queryKey: talentKeys.benchStrength() })
    },
  })
}

export function useAddSuccessionCandidate() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ planId, data }: { planId: string; data: SuccessionCandidateCreate }) =>
      talentReviewService.addSuccessionCandidate(planId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentKeys.succession() })
      queryClient.invalidateQueries({ queryKey: talentKeys.benchStrength() })
    },
  })
}

// ============================================================================
// KEY PERSONS HOOKS
// ============================================================================

export function useKeyPersons(filters?: {
  review_year?: number
  retention_risk?: RetentionRisk
}) {
  return useQuery({
    queryKey: talentKeys.keyPersonsList(filters),
    queryFn: () => talentReviewService.listKeyPersons(filters),
    staleTime: 5 * 60 * 1000,
  })
}

export function useDesignateKeyPerson() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: KeyPersonCreate) => talentReviewService.designateKeyPerson(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentKeys.keyPersons() })
    },
  })
}

// ============================================================================
// HIPO HOOKS
// ============================================================================

export function useHIPOs(filters?: {
  review_year?: number
  category?: HIPOCategory
}) {
  return useQuery({
    queryKey: talentKeys.hiposList(filters),
    queryFn: () => talentReviewService.listHIPOs(filters),
    staleTime: 5 * 60 * 1000,
  })
}

// ============================================================================
// BENCH STRENGTH HOOKS
// ============================================================================

export function useBenchStrength(reviewYear: number, analysisLevel?: string) {
  return useQuery({
    queryKey: talentKeys.benchStrengthAnalysis(reviewYear, analysisLevel),
    queryFn: () => talentReviewService.getBenchStrength(reviewYear, analysisLevel),
    enabled: !!reviewYear,
    staleTime: 10 * 60 * 1000,
  })
}

// ============================================================================
// TALENT REVIEW SESSION HOOKS
// ============================================================================

export function useTalentReviewSessions(
  filters?: {
    review_year?: number
    status?: string
    org_unit_id?: string
    scope?: 'mine' | 'all'
    q?: string
    limit?: number
  },
  options?: { refetchInterval?: number | false | ((query: any) => number | false) },
) {
  return useQuery({
    queryKey: talentKeys.sessionsList(filters),
    queryFn: () => talentReviewService.listSessions(filters as any),
    staleTime: 5 * 60 * 1000,
    refetchInterval: options?.refetchInterval as any,
  })
}

/** The One-Up's People Leaders and where each currently sits, for the
 *  scheduling dialog. Fetched only while the dialog is open. */
export function useAvailableTalentManagers(
  facilitatorId: string | undefined,
  reviewYear: number,
  enabled = true,
  excludeSessionId?: string,
) {
  return useQuery({
    queryKey: ['talent', 'available-managers', facilitatorId || '', reviewYear, excludeSessionId || ''],
    queryFn: () => talentReviewService.getAvailableManagers(facilitatorId!, reviewYear, excludeSessionId),
    enabled: enabled && !!facilitatorId,
    staleTime: 60 * 1000,
  })
}

/** The placement checklist roster for one People Leader in one session. */
export function usePlacementRoster(sessionId: string | undefined, managerId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['talent', 'placement-roster', sessionId || '', managerId || ''],
    queryFn: () => talentReviewService.getPlacementRoster(sessionId!, managerId),
    enabled: enabled && !!sessionId && !!managerId,
    staleTime: 30 * 1000,
  })
}

/** Scope and filters for the talent map. Filters apply server-side; the
 *  facets in the response are always computed over the unfiltered scope. */
export interface TalentGridQueryParams {
  reviewYear: number
  sessionId?: string
  managerId?: string
  function?: string
  department?: string
  campus?: string
  position_type?: string
  leader_id?: string
  one_up_id?: string
}

/** Query key for the talent map — `talentGridKey()` matches every grid
 *  query (for invalidation after a move), `talentGridKey(params)` one. */
export const talentGridKey = (params?: TalentGridQueryParams) =>
  params ? (['talent', 'grid', params] as const) : (['talent', 'grid'] as const)

/** The talent map for a year — a session's placements when sessionId is
 *  given, otherwise the published positions. Filter changes keep the last
 *  result on screen while the next one loads. */
export function useTalentGrid(
  params: TalentGridQueryParams,
  enabled = true,
  options?: { refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: talentGridKey(params),
    // Live in the room: the chair sees moves the HRBP records without refreshing.
    refetchInterval: options?.refetchInterval,
    queryFn: () =>
      talentReviewService.getTalentGrid({
        review_year: params.reviewYear,
        session_id: params.sessionId,
        manager_id: params.managerId,
        function: params.function,
        department: params.department,
        campus: params.campus,
        position_type: params.position_type,
        leader_id: params.leader_id,
        one_up_id: params.one_up_id,
      }),
    enabled: enabled && !!params.reviewYear,
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  })
}

export function useSubmitPlacements() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sessionId, body }: { sessionId: string; body: PlacementBatchInput }) =>
      talentReviewService.submitPlacements(sessionId, body),
    onSuccess: (roster) => {
      queryClient.setQueryData(['talent', 'placement-roster', roster.session_id, roster.manager_id], roster)
      // Session counters and the chair's pre-submission list change too.
      queryClient.invalidateQueries({ queryKey: talentKeys.all })
    },
  })
}

export function useCreateTalentReviewSession() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: TalentReviewSessionCreate) => talentReviewService.createSession(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentKeys.sessions() })
      // Who can still be seated is part of what scheduling changes.
      queryClient.invalidateQueries({ queryKey: ['talent', 'available-managers'] })
    },
  })
}

export function useTalentReviewSession(sessionId: string) {
  return useQuery({
    queryKey: [...talentKeys.sessions(), sessionId],
    queryFn: () => talentReviewService.getSession(sessionId),
    enabled: !!sessionId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useUpdateTalentReviewSession() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ sessionId, data }: { sessionId: string; data: Parameters<typeof talentReviewService.updateSession>[1] }) =>
      talentReviewService.updateSession(sessionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentKeys.sessions() })
      queryClient.invalidateQueries({ queryKey: ['talent', 'available-managers'] })
    },
  })
}

export function useDeleteTalentReviewSession() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ sessionId, force }: { sessionId: string; force?: boolean }) =>
      talentReviewService.deleteSession(sessionId, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentKeys.sessions() })
      queryClient.invalidateQueries({ queryKey: ['talent', 'available-managers'] })
    },
  })
}

/** Cancel a session that has not started (the chair, or HR). Its People Leaders become free to
 *  seat elsewhere, so the offer lists refresh with the sessions. */
export function useCancelTalentSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ sessionId, reason }: { sessionId: string; reason?: string }) =>
      talentReviewService.cancelSession(sessionId, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: talentKeys.all }),
  })
}

// Phase transition hooks
export function useBeginPreReview() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (sessionId: string) => talentReviewService.beginPreReview(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentKeys.sessions() })
    },
  })
}

export function useGenerateTalentReport() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (sessionId: string) => talentReviewService.generateReport(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentKeys.sessions() })
    },
  })
}

export function useStartTalentSession() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ sessionId, releaseUnsubmitted, includeDraftsOf }: {
      sessionId: string
      releaseUnsubmitted?: boolean
      /** People Leaders whose drafts place everyone, handed in as they stand before the start. */
      includeDraftsOf?: string[]
    }) => talentReviewService.startSession(sessionId, releaseUnsubmitted, includeDraftsOf),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentKeys.sessions() })
      // Starting releases the People Leaders who sent nothing, which frees them
      // for another session; the scheduling dialog kept showing them as taken
      // (Rozanne, 10 Sep 2026).
      queryClient.invalidateQueries({ queryKey: ['talent', 'available-managers'] })
    },
  })
}

export function useEndTalentSession() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (sessionId: string) => talentReviewService.endSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentKeys.sessions() })
    },
  })
}

/** The gateway refuses a repeated identical request with a 409 and this exact
    text. It means the first press is still running, not that anything failed —
    pressing Complete twice showed the room an error for a session that was
    completing normally (HR process owner, 9 Sep 2026). */
const DUPLICATE_REQUEST = 'Duplicate request detected. Please wait a moment before retrying.'

function isDuplicateRequest(e: unknown): boolean {
  const err = e as { status?: number; detail?: string; message?: string }
  return err?.status === 409 && (err?.detail === DUPLICATE_REQUEST || err?.message === DUPLICATE_REQUEST)
}


export function useCompleteTalentSession() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ sessionId, summary }: { sessionId: string; summary?: string }) => {
      try {
        return await talentReviewService.completeSession(sessionId, summary)
      } catch (e) {
        if (isDuplicateRequest(e)) return null
        throw e
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: talentKeys.all })
    },
  })
}

// Participant hooks
export function useTalentReviewParticipants(sessionId: string) {
  return useQuery({
    queryKey: [...talentKeys.sessions(), sessionId, 'participants'],
    queryFn: () => talentReviewService.listParticipants(sessionId),
    enabled: !!sessionId,
    staleTime: 2 * 60 * 1000,
  })
}

/** Change who is in a session after it was scheduled — People Leaders while it
 *  is still being prepared, observers at any time (HR process owner, 9 Sep 2026). */
export function useSetSessionAttendees() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sessionId, managers, observers }: { sessionId: string; managers?: string[]; observers?: string[] }) =>
      talentReviewService.setAttendees(sessionId, { managers, observers }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: talentKeys.all }),
  })
}


export function useAddTalentParticipant() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ sessionId, data }: { sessionId: string; data: Parameters<typeof talentReviewService.addParticipant>[1] }) =>
      talentReviewService.addParticipant(sessionId, data),
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: [...talentKeys.sessions(), sessionId, 'participants'] })
      queryClient.invalidateQueries({ queryKey: talentKeys.sessions() })
      queryClient.invalidateQueries({ queryKey: ['talent', 'available-managers'] })
    },
  })
}

export function useRemoveTalentParticipant() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ sessionId, participantId }: { sessionId: string; participantId: string }) =>
      talentReviewService.removeParticipant(sessionId, participantId),
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: [...talentKeys.sessions(), sessionId, 'participants'] })
      queryClient.invalidateQueries({ queryKey: talentKeys.sessions() })
      queryClient.invalidateQueries({ queryKey: ['talent', 'available-managers'] })
    },
  })
}

// Pre-submission hooks
export function useTalentPreSubmissions(sessionId: string, params?: { manager_id?: string; status?: string }) {
  return useQuery({
    queryKey: [...talentKeys.sessions(), sessionId, 'submissions', params],
    queryFn: () => talentReviewService.listPreSubmissions(sessionId, params as any),
    enabled: !!sessionId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useCreateTalentPreSubmission() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ sessionId, data }: { sessionId: string; data: Parameters<typeof talentReviewService.createPreSubmission>[1] }) =>
      talentReviewService.createPreSubmission(sessionId, data),
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: [...talentKeys.sessions(), sessionId, 'submissions'] })
      queryClient.invalidateQueries({ queryKey: talentKeys.sessions() })
    },
  })
}

export function useUpdateTalentPreSubmission() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ submissionId, data }: { submissionId: string; data: Parameters<typeof talentReviewService.updatePreSubmission>[1] }) =>
      talentReviewService.updatePreSubmission(submissionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentKeys.sessions() })
    },
  })
}

export function useSubmitTalentPreSubmission() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (submissionId: string) => talentReviewService.submitPreSubmission(submissionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentKeys.sessions() })
    },
  })
}

export function useFlagTalentPreSubmission() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ submissionId, flagReason, flaggedBy }: { submissionId: string; flagReason: string; flaggedBy: string }) =>
      talentReviewService.flagPreSubmission(submissionId, flagReason, flaggedBy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentKeys.sessions() })
    },
  })
}

// Decision hooks
export function useTalentDecisions(sessionId: string, decisionType?: string, options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: [...talentKeys.sessions(), sessionId, 'decisions', decisionType],
    queryFn: () => talentReviewService.listDecisions(sessionId, decisionType as any),
    enabled: !!sessionId,
    staleTime: 2 * 60 * 1000,
    refetchInterval: options?.refetchInterval,
  })
}

export function useCreateTalentDecision() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ sessionId, data }: { sessionId: string; data: Parameters<typeof talentReviewService.createDecision>[1] }) =>
      talentReviewService.createDecision(sessionId, data),
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: [...talentKeys.sessions(), sessionId, 'decisions'] })
      queryClient.invalidateQueries({ queryKey: talentKeys.sessions() })
    },
  })
}

// Analytics hooks
export function useTalentSessionAnalytics(sessionId: string) {
  return useQuery({
    queryKey: [...talentKeys.sessions(), sessionId, 'analytics'],
    queryFn: () => talentReviewService.getSessionAnalytics(sessionId),
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000,
  })
}

// ============================================================================
// PRE-SUBMISSION HOOKS
// ============================================================================

/**
 * Fetch pre-submissions for a session
 */
export function usePreSubmissions(sessionId: string, managerId?: string) {
  return useQuery({
    queryKey: [...talentKeys.sessions(), sessionId, 'submissions', managerId],
    queryFn: () => talentReviewService.listPreSubmissions(sessionId, managerId ? { manager_id: managerId } : undefined),
    enabled: !!sessionId,
    staleTime: 2 * 60 * 1000,
  })
}

/**
 * Create a new pre-submission (first save)
 */
export function useCreatePreSubmission() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ sessionId, data }: { 
      sessionId: string
      data: Parameters<typeof talentReviewService.createPreSubmission>[1] 
    }) => talentReviewService.createPreSubmission(sessionId, data),
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: [...talentKeys.sessions(), sessionId, 'submissions'] })
      queryClient.invalidateQueries({ queryKey: talentKeys.sessions() })
    },
  })
}

/**
 * Update an existing pre-submission (save draft)
 */
export function useUpdatePreSubmission() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ submissionId, data }: { 
      submissionId: string
      data: Parameters<typeof talentReviewService.updatePreSubmission>[1] 
    }) => talentReviewService.updatePreSubmission(submissionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentKeys.sessions() })
    },
  })
}

/**
 * Submit a pre-submission for review (final submit)
 */
export function useSubmitPreSubmission() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (submissionId: string) => talentReviewService.submitPreSubmission(submissionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: talentKeys.sessions() })
    },
  })
}

// ============================================================================
// SUCCESSION ROLES HOOKS (from F&O Positions)
// ============================================================================

export interface SuccessionRoleOption {
  id: string
  name: string
  description?: string
  department?: string
  functional_area?: string // Pipeline level from F&O
  position_count: number
}

/**
 * Hook to fetch available succession roles from F&O positions
 * Aggregates positions by title and returns unique roles that have
 * requires_succession_plan OR is_critical_role flag set to true
 * 
 * Uses targeted API calls instead of fetching all positions for efficiency
 */
export function useSuccessionRoles(department?: string, options?: { enabled?: boolean }) {
  return useQuery({
    // Role Master positions are HR-readable only: a One-Up on the Talent tab
    // gets 403 twice (and a "no permission" toast) if this runs for them.
    enabled: options?.enabled ?? true,
    queryKey: [...talentKeys.succession(), 'roles', 'fo', department],
    queryFn: async (): Promise<SuccessionRoleOption[]> => {
      const { organizationService } = await import('@/services/api/organizationService')
      
      // Fetch only positions with critical role OR succession plan flags
      // Two targeted API calls instead of fetching all 5000+ positions
      const [criticalPositions, successionPositions] = await Promise.all([
        organizationService.listPositions({
          status: ['ACTIVE', 'VACANT'],
          is_critical: true,
          limit: 500
        }),
        organizationService.listPositions({
          status: ['ACTIVE', 'VACANT'],
          requires_succession: true,
          limit: 500
        })
      ])
      
      // Merge and deduplicate by position ID
      const positionMap = new Map<number, typeof criticalPositions[0]>()
      for (const pos of [...criticalPositions, ...successionPositions]) {
        if (!positionMap.has(pos.id)) {
          positionMap.set(pos.id, pos)
        }
      }
      const positions = Array.from(positionMap.values())
      
      // Aggregate by title to get unique roles
      const roleMap = new Map<string, SuccessionRoleOption>()
      
      for (const pos of positions) {
        const title = pos.title?.trim()
        if (!title) continue
        
        // Filter by department if specified
        if (department && pos.job_family !== department) continue
        
        if (!roleMap.has(title)) {
          roleMap.set(title, {
            id: title, // Use title as ID since it's the role identifier
            name: title,
            department: pos.job_family || undefined,
            functional_area: pos.functional_area || undefined,
            position_count: 0
          })
        }
        
        const role = roleMap.get(title)!
        role.position_count++
      }
      
      return Array.from(roleMap.values()).sort((a, b) => a.name.localeCompare(b.name))
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Hook to get helper functions from the talent review service
 */
export function useTalentReviewHelpers() {
  return {
    getBoxLabel: talentReviewService.getBoxLabel,
    isHIPOPosition: talentReviewService.isHIPOPosition,
    getHIPOCategory: talentReviewService.getHIPOCategory,
    getRetentionRiskColor: talentReviewService.getRetentionRiskColor,
    getReadinessColor: talentReviewService.getReadinessColor,
  }
}

// ---------------------------------------------------------------------------
// Successors for the leader's own role
// ---------------------------------------------------------------------------
export function useSuccessorNominations(sessionId: string | undefined, managerId?: string, enabled = true) {
  return useQuery({
    queryKey: ['talent', 'successors', sessionId || '', managerId || '*'],
    queryFn: () => talentReviewService.getSuccessorNominations(sessionId!, managerId),
    enabled: enabled && !!sessionId,
    staleTime: 30 * 1000,
  })
}

export function useSaveSuccessorNominations() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sessionId, body }: { sessionId: string; body: SuccessorNominationBatchInput }) =>
      talentReviewService.saveSuccessorNominations(sessionId, body),
    onSuccess: (list) => {
      queryClient.setQueryData(['talent', 'successors', list.session_id, list.manager_id || '*'], list)
      queryClient.invalidateQueries({ queryKey: ['talent', 'successors', list.session_id] })
    },
  })
}

export function useDecideSuccessor() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sessionId, nominationId, status, note, readiness }: { sessionId: string; nominationId: string; status: SuccessorStatus; note?: string; readiness?: SuccessorReadiness }) =>
      talentReviewService.decideSuccessor(sessionId, nominationId, { status, note, readiness }),
    onSuccess: (_nomination, vars) => {
      queryClient.invalidateQueries({ queryKey: ['talent', 'successors', vars.sessionId] })
    },
  })
}

// ---------------------------------------------------------------------------
// The campus General Manager's input space (a campus GM and the HR process owner, 8 Sep 2026)
// ---------------------------------------------------------------------------

/** The campuses this viewer may open. Empty for someone who leads nobody. */
export function useCampusOptions(reviewYear: number, enabled = true) {
  return useQuery({
    queryKey: ['talent', 'campuses', reviewYear],
    queryFn: () => talentReviewService.listCampuses(reviewYear),
    enabled: enabled && !!reviewYear,
    staleTime: 5 * 60 * 1000,
  })
}

/** Everyone at one campus, with the input already given on them. */
export function useCampusView(reviewYear: number, campus: string | null) {
  return useQuery({
    queryKey: ['talent', 'campus-view', reviewYear, campus || ''],
    queryFn: () => talentReviewService.getCampusView(reviewYear, campus!),
    enabled: !!reviewYear && !!campus,
    staleTime: 60 * 1000,
  })
}

export function useSaveCampusInput() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ employeeId, body }: { employeeId: string; body: CampusInputWrite }) =>
      talentReviewService.saveCampusInput(employeeId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['talent', 'campus-view'] })
      queryClient.invalidateQueries({ queryKey: ['talent', 'campuses'] })
      // The room reads the input on the person's card.
      queryClient.invalidateQueries({ queryKey: talentKeys.all })
    },
  })
}

/** The platform directory — anyone active, by name, email, title or personnel number. */
export function useTalentPeopleSearch(q: string, enabled = true) {
  return useQuery({
    queryKey: ['talent', 'people', q.trim().toLowerCase()],
    queryFn: () => talentReviewService.searchPeople(q.trim()),
    enabled: enabled && q.trim().length >= 2,
    staleTime: 5 * 60 * 1000,
  })
}

/** The room adds a successor for a leader's role during the session (Victor, 8 Sep 2026). */
export function useAddRoomSuccessor() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sessionId, body }: { sessionId: string; body: RoomSuccessorInput }) => talentReviewService.addRoomSuccessor(sessionId, body),
    onSuccess: (_n, vars) => queryClient.invalidateQueries({ queryKey: ['talent', 'successors', vars.sessionId] }),
  })
}

/** The room amends a high-potential's growth details while the session is live. */
export function useAmendGrowthDetails() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sessionId, employeeId, body }: { sessionId: string; employeeId: string; body: Parameters<typeof talentReviewService.amendGrowthDetails>[2] }) =>
      talentReviewService.amendGrowthDetails(sessionId, employeeId, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: talentKeys.all }),
  })
}

/** The people every Annual I&G Review invites by default (HR's standing invitees). */
/** The One-Ups HR can schedule for. Fetched only for HR, while the dialog is open. */
export function useTalentChairs(reviewYear: number, enabled = true) {
  return useQuery({
    queryKey: [...talentKeys.all, 'chairs', reviewYear],
    queryFn: () => talentReviewService.listChairs(reviewYear),
    enabled: enabled && !!reviewYear,
    staleTime: 5 * 60 * 1000,
  })
}

export function useDefaultObservers(enabled = true) {
  return useQuery({
    queryKey: [...talentKeys.all, 'default-observers'],
    queryFn: () => talentReviewService.listDefaultObservers(),
    enabled,
    staleTime: 10 * 60 * 1000,
  })
}

export function useHrPeople(role: 'hrbp' | 'hr_admin' | 'hr_manager' | 'hr_specialist' = 'hrbp', enabled = true) {
  return useQuery({
    queryKey: ['identity', 'users-by-role', role],
    queryFn: () => roleMasterService.listUsersByRole(role),
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

// ---------------------------------------------------------------------------
// The room's safety net: undo, the agenda, reopen
// ---------------------------------------------------------------------------
export function useUndoTalentDecision() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sessionId, decisionId }: { sessionId: string; decisionId: string }) =>
      talentReviewService.undoDecision(sessionId, decisionId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: talentKeys.all }),
  })
}

export function useSetAgendaStep() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ sessionId, step, done }: { sessionId: string; step: AgendaStepId; done: boolean }) => {
      try {
        return await talentReviewService.setAgendaStep(sessionId, step, done)
      } catch (e) {
        // Ticking the same step twice in a second is one tick.
        if (isDuplicateRequest(e)) return null
        throw e
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: talentKeys.all }),
  })
}

export function useReopenTalentSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sessionId, reason }: { sessionId: string; reason: string }) =>
      talentReviewService.reopenSession(sessionId, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: talentKeys.all }),
  })
}

// ---------------------------------------------------------------------------
// The HIPO worklist for the Good Governance People Forum
// ---------------------------------------------------------------------------
export function useHipoWorklist(reviewYear: number, enabled = true) {
  return useQuery({
    queryKey: ['talent', 'hipo-worklist', reviewYear],
    queryFn: () => talentReviewService.getHipoWorklist(reviewYear),
    enabled,
    staleTime: 60 * 1000,
  })
}

export function useRecordForumReview() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, reviewYear, note }: { userId: string; reviewYear: number; note?: string }) =>
      talentReviewService.recordForumReview(userId, reviewYear, note),
    onSuccess: (_r, vars) => queryClient.invalidateQueries({ queryKey: ['talent', 'hipo-worklist', vars.reviewYear] }),
  })
}
